import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import { InPersonLayout } from 'room/common/presentation/video-layout/in-person-layout'
import { useSimpleGroupingTask } from 'features/simple-grouping/use-simple-grouping-task'

const Card = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	row-gap: 24px;
	text-align: center;
	width: 100%;
	height: 100%;
	padding: 40px;
`

/**
 * Sized for a projector at the back of a room, not for a laptop an arm's
 * length away.
 */
const Heading = styled.h1`
	margin: 0;
	color: ${colors.indigoBlue};
	font-size: 48px;
	font-weight: 700;
	line-height: 1.2;

	${ui.responsive.desktop1440} {
		font-size: 64px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 80px;
	}
`

/**
 * The task itself. Deliberately smaller than the heading: the heading says
 * what is happening and is read once, the task is what the room keeps looking
 * back at, and two competing display sizes on one screen read as a mistake.
 */
const TaskName = styled.h2`
	margin: 0;
	color: ${colors.darkIndigo};
	font-size: 32px;
	font-weight: 600;
	line-height: 1.25;
	max-width: 20ch;

	${ui.responsive.desktop1440} {
		font-size: 40px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 48px;
	}
`

/**
 * `colors.blackish` is the app's body-text colour, and on a projector that
 * matters more than it does on a laptop. Not `colors.darkGray`, which despite
 * the name is `#d8d8e5` - near-white, and unreadable from the back of a room.
 */
const TaskDescription = styled.p`
	margin: 0;
	color: ${colors.blackish};
	font-size: 22px;
	font-weight: 400;
	line-height: 1.45;
	/* Long enough to hold a real instruction, short enough to stay readable
	   from the back of the room rather than running the width of a projector. */
	max-width: 34ch;
	white-space: pre-wrap;

	${ui.responsive.desktop1440} {
		font-size: 26px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 32px;
	}
`

/**
 * The group-work screen. `SimpleGrouping` is only ever enabled by a stationary
 * interaction scheme, so this is an in-person screen by construction and uses
 * `InPersonLayout` rather than a video grid.
 *
 * The heading always shows. The task name and description show when group work
 * has them - see `useSimpleGroupingTask` for when they are absent.
 */
export const SimpleGroupingFeature: FC = () => {
	const task = useSimpleGroupingTask()

	return (
		<InPersonLayout>
			<Card>
				<Heading>
					<intl.Translate>features.simple-grouping.title</intl.Translate>
				</Heading>
				{task?.name ? <TaskName>{task.name}</TaskName> : null}
				{task?.description ? (
					<TaskDescription>{task.description}</TaskDescription>
				) : null}
			</Card>
		</InPersonLayout>
	)
}
